import { Plus, Trash2 } from 'lucide-react';
import type { TrainingQuestionDef } from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { QuestionCommonFields } from './QuestionCommonFields';

type ShortTextQuestion = Extract<TrainingQuestionDef, { type: 'short_text' }>;

/**
 * Free-text editor: the prompt + points, the shape of the answer box, and
 * (when the module is marked) the list of accepted answers. Matching is case-
 * and whitespace-insensitive, so authors only need genuinely different
 * spellings (e.g. "PPE", "personal protective equipment").
 *
 * An unmarked assessment has no answer key, and this is the editor behind its
 * "supporting comments" box: multiline, and usually optional.
 */
export function ShortTextQuestionEditor({
  question,
  onChange,
  graded = true,
}: {
  question: ShortTextQuestion;
  onChange: (next: ShortTextQuestion) => void;
  graded?: boolean;
}) {
  const accepted = question.accepted ?? [];

  function setAccepted(index: number, value: string) {
    onChange({
      ...question,
      accepted: accepted.map((a, i) => (i === index ? value : a)),
    });
  }

  return (
    <div className="space-y-3">
      <QuestionCommonFields
        prompt={question.prompt}
        points={question.points}
        optional={question.optional}
        onPrompt={(prompt) => onChange({ ...question, prompt })}
        onPoints={(points) => onChange({ ...question, points })}
        graded={graded}
        onOptional={(optional) => onChange({ ...question, optional })}
      />
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 pb-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={question.multiline === true}
            onChange={(e) => onChange({ ...question, multiline: e.target.checked })}
          />
          <span>Large comments box</span>
        </label>
        <label className="min-w-56 flex-1 text-sm">
          <span className="font-medium text-fg-muted">Placeholder (optional)</span>
          <input
            value={question.placeholder ?? ''}
            onChange={(e) => onChange({ ...question, placeholder: e.target.value })}
            placeholder="e.g. Tell us what needs sorting"
            className={SHARED_INPUT_CLASS}
          />
        </label>
      </div>
      {graded ? (
        <div className="space-y-2">
          <span className="text-sm font-medium text-fg-muted">
            Accepted answers (case and spacing are ignored)
          </span>
          {accepted.map((answer, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={answer}
                onChange={(e) => setAccepted(i, e.target.value)}
                placeholder="An answer that counts as correct"
                className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
              />
              <button
                type="button"
                aria-label="Remove answer"
                disabled={accepted.length <= 1}
                onClick={() =>
                  onChange({ ...question, accepted: accepted.filter((_, x) => x !== i) })
                }
                className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Plus className="h-4 w-4" />}
            onClick={() => onChange({ ...question, accepted: [...accepted, ''] })}
          >
            Add accepted answer
          </Button>
        </div>
      ) : null}
    </div>
  );
}
